import { onMounted, ref, Ref } from "vue";
import { IGlobalWarning, IPaymentProvider, IPaymentProviderParams } from "@/types/IPaymentProvider";
import { IInstallment, IInstallmentParams } from "@/types/IInstallment";
import { APIInstallmentsData } from "@/services/InstallmentsDataService";
import { APIPaymentProvidersData } from "@/services/PaymentProvidersDataService";
import { ISuperPacmanInstallment, ISuperPacmanFetchInstallmentRequest, ISuperPacmanReferenceResponse, ISuperPacmanReferenceRequest } from "@/types/ISuperPacman";
import { APISuperPacmanInstallment } from "./SuperPacmanInstallmentDataService";
import { APISuperPacmanReference } from "./SuperPacmanReferenceDataService";
import { identifyDevice } from "@/customerio";

export interface IFetchDataSource<T, Params> {
  fetchData: (params: Params) => Promise<T>;
}

/**
 * Define a generic function that fetches Installments data from a data source.
 * @param type: string -> deviceTag
 */
export interface FetchInstallments
  extends IFetchDataSource<IInstallment, IInstallmentParams> {
  fetchData(params: {
    deviceTag: string;
    paymentOption: string;
  }): Promise<IInstallment>;
}

export interface FetchSuperPacmanInstallment extends IFetchDataSource<ISuperPacmanInstallment, ISuperPacmanFetchInstallmentRequest> {
  fetchData(params: {
    deviceTag: string;
    optionName: string;
  }): Promise<ISuperPacmanInstallment>;
}

export interface FetchSuperPacmanReference extends IFetchDataSource<ISuperPacmanReferenceResponse, ISuperPacmanReferenceRequest> {
  fetchData(params: {
    financeProductType: string;
    financeProductId: number;
    countryCode: string;
    currency: string;
    amount: string;
    paymentType: string,
    paymentSubType: string | null;
  }): Promise<ISuperPacmanReferenceResponse>;
}

/**
 * Fetches the Installments.
 * @param df
 * @param params
 * @param error
 */
async function fetchInstallments(
  df: FetchInstallments,
  params: IInstallmentParams,
  error: Ref<boolean>
): Promise<IInstallment> {
  let response: IInstallment = {
    currencySymbol: "",
    country: "",
    currency: "",
    installments: [],
  };

  try {
    response = await df.fetchData(params);
  } catch (e) {
    console.error(e);
    error.value = true;
  }

  return response;
}

export async function getInstallments(params: IInstallmentParams) {
  const installments = ref<IInstallment>();
  const error = ref(false);

  installments.value = await fetchInstallments(
    new APIInstallmentsData(),
    params,
    error
  );

  return { installments, error };
}

export async function fetchSuperPacmanInstallment(
  functionInterface: FetchSuperPacmanInstallment,
  request: ISuperPacmanFetchInstallmentRequest,
  error: Ref<boolean>
): Promise<ISuperPacmanInstallment> {
  let response: ISuperPacmanInstallment = {
    financeProduct: {
      financeProductType: "",
      financeProductId: null,
      financeOrderId: null,
      creditLineId: null,
      country: "",
      currency: "",
      customerName: "",
      deviceTag: "",
      deviceModel: "",
      deviceImei: "",
      financeOrderPayoffAmountIncludingCumulativeBalance: "",
      creditLinePayoffAmount: "",
      minimumPaymentAmount: "",
      fullPayoffAmount: "",
      foMinPaymentWithCbPlusCLPayoffAmount: ""
    },
    cumulativeBalance: {
      cumulativeBalanceAmount: "",
    },
    validThrough: {
      currentValidThrough: "0"
    },
    daysToAmount: []
  };

  try {
    response = await functionInterface.fetchData(request);
  } catch (e) {
    console.error(e);
    error.value = true;
  }
  return response;
}

export async function fetchSuperPacmanReference(
  functionInterface: FetchSuperPacmanReference,
  request: ISuperPacmanReferenceRequest,
  error: Ref<boolean>
): Promise<ISuperPacmanReferenceResponse> {
  let response: ISuperPacmanReferenceResponse = {
    financeProductType: "",
    financeProductId: null,
    country: "",
    currency: "",
    amount: "",
    paymentType: "",
    paymentSubType: "",
    paymentReferenceNumber1: "",
    paymentReferenceNumber2: "",
    paymentReferenceExpiration: null,
    validThrough: undefined,
  }

  try {
    response = await functionInterface.fetchData(request);
  } catch (e) {
    console.error(e);
    error.value = true;
  }
  return response;
}

export function useInstallments(params: IInstallmentParams) {
  const installments = ref<IInstallment>();
  const error = ref(false);

  async function fetchData() {
    installments.value = await fetchInstallments(
      new APIInstallmentsData(),
      params,
      error
    );
    installments.value.installments.sort((a, b) => a.amount - b.amount);
  }
  
  onMounted(() => {
    fetchData();
  });

  return { installments, error };
}

export function useSuperPacmanInstallments(request: ISuperPacmanFetchInstallmentRequest) {
  const superPacmanInstallment = ref<ISuperPacmanInstallment>();
  const error = ref(false);

  async function fetchData() {
    superPacmanInstallment.value = await fetchSuperPacmanInstallment(
      new APISuperPacmanInstallment(),
      request,
      error
    );
    if (!error.value) {
      // Backend login succeeded above -> safe to identify to Customer.io.
      identifyDevice(request.deviceTag);
    }
  }

  onMounted(() => {
    fetchData();
  });

  return { superPacmanInstallment, error };
}

export function useSuperPacmanReference(request: ISuperPacmanReferenceRequest) {
  const superPacmanReferenceResponse = ref<ISuperPacmanReferenceResponse>();
  const error = ref(false);

  async function fetchData() {
    superPacmanReferenceResponse.value = await fetchSuperPacmanReference(
      new APISuperPacmanReference(),
      request,
      error
    )
  }

  onMounted(() => {
    fetchData();
  });

  return { superPacmanReferenceResponse, error }
}

/**
 * Define a generic function that fetches PaymentProviders data from a data source.
 * @param type: params -> { country, paymentOption }
 */
export interface FetchPaymentProviders {
  fetchData(params: {
    country: string;
    paymentOption: string;
  }): Promise<{ providers: IPaymentProvider[]; globalWarning: IGlobalWarning | null }>;
}

/**
 * Fetches the PaymentProviders.
 * @param df
 * @param params
 */
async function fetchPaymentProviders(
  df: FetchPaymentProviders,
  params: IPaymentProviderParams,
): Promise<{ providers: IPaymentProvider[]; globalWarning: IGlobalWarning | null }> {
  try {
    return await df.fetchData(params);
  } catch (error) {
    console.error(error);
    return { providers: [], globalWarning: null };
  }
}

export function usePaymentProviders(params: IPaymentProviderParams) {
  const paymentProviders = ref<IPaymentProvider[]>([]);
  const globalWarning = ref<IGlobalWarning | null>(null);

  async function fetchData() {
    const result = await fetchPaymentProviders(new APIPaymentProvidersData(), params);
    paymentProviders.value = result.providers;
    globalWarning.value = result.globalWarning;
  }

  onMounted(() => {
    fetchData();
  });
  
  return { paymentProviders, globalWarning };
}
